import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { BooleanControl, NumberControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./OverlayPlayground.module.scss";

const clampSelectedCount = (value: number) =>
    Number.isFinite(value) ? Math.min(Math.max(value, 1), 99) : undefined;

const mockRows = [
    { id: 1, description: "Figma — annual subscription", amount: "-$1,200.00" },
    { id: 2, description: "Stripe payout", amount: "$8,430.19" },
    { id: 3, description: "AWS — March usage", amount: "-$2,014.55" },
    { id: 4, description: "Notion — team seats", amount: "-$96.00" },
    { id: 5, description: "Client retainer — Northwind", amount: "$4,500.00" },
    { id: 6, description: "Delta Airlines — SFO/JFK", amount: "-$612.40" },
    { id: 7, description: "Gusto — payroll", amount: "-$21,880.03" },
    { id: 8, description: "Apple Store — MacBook Pro", amount: "-$3,499.00" },
];

export const CommandIslandPlayground = () => {
    const [selectedCount, setSelectedCount] = useState(3);
    const [isOpen, setIsOpen] = useState(true);
    const [hasSelectionCount, setHasSelectionCount] = useState(true);
    const [isAlignedEnd, setIsAlignedEnd] = useState(false);
    const [isSticky, setIsSticky] = useState(true);
    const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
    const [lastAction, setLastAction] = useState<string>();

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Selection</h2>
                        <NumberControl
                            label="Selected rows"
                            value={selectedCount}
                            min={1}
                            max={99}
                            onChange={(value) => {
                                const nextSelectedCount =
                                    clampSelectedCount(value);
                                if (nextSelectedCount !== undefined) {
                                    setSelectedCount(nextSelectedCount);
                                }
                            }}
                        />
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.controls}>
                            <BooleanControl
                                label="Open"
                                checked={isOpen}
                                onChange={setIsOpen}
                            />
                            <BooleanControl
                                label="Selection count"
                                checked={hasSelectionCount}
                                onChange={setHasSelectionCount}
                            />
                            <BooleanControl
                                label="Align actions to end"
                                checked={isAlignedEnd}
                                onChange={setIsAlignedEnd}
                            />
                            <BooleanControl
                                label="Sticky"
                                checked={isSticky}
                                onChange={setIsSticky}
                            />
                            <BooleanControl
                                label="Disable delete"
                                checked={isDeleteDisabled}
                                onChange={setIsDeleteDisabled}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${selectedCount} selected`}
                width="wide"
                output={JSON.stringify(
                    {
                        isOpen,
                        alignEnd: isAlignedEnd,
                        sticky: isSticky,
                        showcase: {
                            selectedCount,
                            hasSelectionCount,
                            isDeleteDisabled,
                            lastAction: lastAction ?? null,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={styles.commandStage}>
                    <div className={styles.commandStageRows}>
                        {mockRows.map((row) => (
                            <div
                                key={row.id}
                                className={styles.commandStageRow}
                            >
                                <span>{row.description}</span>
                                <span>{row.amount}</span>
                            </div>
                        ))}
                    </div>
                    <CommandIsland
                        isOpen={isOpen}
                        alignEnd={isAlignedEnd}
                        sticky={isSticky}
                    >
                        <HStack gap={8}>
                            {hasSelectionCount && (
                                <CommandIslandTotal>
                                    {selectedCount} selected
                                </CommandIslandTotal>
                            )}
                        </HStack>
                        <HStack gap={8}>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => setLastAction("Categorize")}
                            >
                                Categorize
                            </Button>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => setLastAction("Mark reviewed")}
                            >
                                Mark reviewed
                            </Button>
                            <Tooltip
                                content={
                                    isDeleteDisabled
                                        ? "Deletion is unavailable"
                                        : undefined
                                }
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    disabled={isDeleteDisabled}
                                    onClick={() => setLastAction("Delete")}
                                >
                                    Delete
                                </Button>
                            </Tooltip>
                        </HStack>
                    </CommandIsland>
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
